// src/components/auth/VerifyEmail.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from "@/components/ui/use-toast";

const VerifyEmail = () => {
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const { user, verifyEmail } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Countdown timer for resending verification email
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  // Check verification status
  useEffect(() => {
    const interval = setInterval(async () => {
      if (user) {
        await user.reload();
        if (user.emailVerified) {
          navigate('/chat');
        }
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [user, navigate]);

  const handleResendVerification = async () => {
    try {
      setLoading(true);
      await verifyEmail();
      setTimeLeft(60);
      toast({
        title: "Verification email sent",
        description: "Please check your inbox for the verification link.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Verify your email
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            We've sent a verification email to{' '}
            <span className="font-medium text-blue-600">{user?.email}</span>
          </p>
        </div>

        <div className="rounded-md bg-yellow-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Please verify your email to continue
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  Check your inbox and click the verification link in the email we sent you.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <button
            onClick={handleResendVerification}
            disabled={loading || timeLeft > 0}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? 'Sending...'
              : timeLeft > 0
              ? `Resend in ${timeLeft}s`
              : 'Resend verification email'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;